import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Music, Heart, Users } from 'lucide-react';

const LoginModal = ({ open, onOpenChange, title, message, onSuccess }) => {
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password);
        toast.success('Account created! Welcome!');
      }
      onOpenChange(false);
      // Execute callback if provided, otherwise reload page
      if (onSuccess) {
        onSuccess();
      } else {
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.message || `${mode === 'login' ? 'Login' : 'Signup'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      toast.error('Google login failed');
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-2 border-border max-w-md p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-primary/10 border-b border-border p-6 text-center">
          <DialogTitle className="font-accent text-3xl text-foreground mb-2">
            {mode === 'login' ? 'WELCOME BACK' : 'JOIN THE SCENE'}
          </DialogTitle>
          <p className="font-mono text-sm text-muted-foreground">
            {title || (mode === 'login' 
              ? 'Sign in to access full features' 
              : 'Create a free account to get started')}
          </p>
        </div>

        {/* Features Teaser */}
        {message && (
          <div className="px-6 pt-4">
            <p className="font-mono text-sm text-muted-foreground text-center">{message}</p>
          </div>
        )}

        <div className="p-6 space-y-4">
          {/* Quick Features */}
          <div className="grid grid-cols-3 gap-2 pb-4 border-b border-border">
            <div className="text-center">
              <Music className="w-5 h-5 mx-auto text-primary mb-1" />
              <span className="font-mono text-[10px] text-muted-foreground uppercase">Discover</span>
            </div>
            <div className="text-center">
              <Heart className="w-5 h-5 mx-auto text-secondary mb-1" />
              <span className="font-mono text-[10px] text-muted-foreground uppercase">Save</span>
            </div>
            <div className="text-center">
              <Users className="w-5 h-5 mx-auto text-primary mb-1" />
              <span className="font-mono text-[10px] text-muted-foreground uppercase">Playlists</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="modal-email" className="font-mono text-xs uppercase">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="modal-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-10 bg-input border-2 border-border font-mono"
                  data-testid="modal-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="modal-password" className="font-mono text-xs uppercase">
                  Password
                </Label>
                {mode === 'login' && (
                  <Link 
                    to="/forgot-password" 
                    className="font-mono text-[10px] text-primary hover:underline"
                    onClick={() => onOpenChange(false)}
                    data-testid="modal-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="modal-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={mode === 'signup' ? 6 : undefined}
                  className="pl-10 bg-input border-2 border-border font-mono"
                  data-testid="modal-password-input"
                />
              </div>
              {mode === 'signup' && (
                <p className="font-mono text-[10px] text-muted-foreground">
                  Minimum 6 characters
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest py-5"
              data-testid="modal-submit-btn"
            >
              {loading 
                ? (mode === 'login' ? 'Signing in...' : 'Creating account...') 
                : (mode === 'login' ? 'Sign In' : 'Create Account')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-4 font-mono text-muted-foreground uppercase">
                Or
              </span>
            </div>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-2 border-foreground text-foreground hover:bg-foreground hover:text-background font-mono uppercase tracking-widest"
            data-testid="modal-google-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Switch Mode */}
          <p className="text-center font-mono text-sm text-muted-foreground pt-2">
            {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button 
              type="button"
              onClick={switchMode}
              className="text-primary hover:underline"
              data-testid="modal-switch-mode"
            >
              {mode === 'login' ? 'Sign Up' : 'Log In'}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
